import { useMemo } from "react";
import { MiniCandleChart } from "@/components/MiniCandleChart";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Zap, Copy } from "lucide-react";
import { normalizeCoinImageUri } from "@/lib/imageUtils";

export interface AlphaTargetData {
  mint: string;
  coin: string;
  name?: string | null;
  desc?: React.ReactNode | null;
  confidence?: number;
  whaleSol?: number;
  bondingPct?: number;
  mcUsd?: number | null;
  multiplier?: number;
  imageUri?: string | null;
  badge?: string;
  top1?: number | null;
  creatorPct?: number | null;
  baselineMcUsd?: number | null;
  at?: number | null;
}

function fmtTimeAgo(timestamp?: number | null) {
  if (!timestamp) return "";
  const mins = Math.max(1, Math.round((Date.now() - timestamp) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function AlphaTokenRow({
  target,
  isSelected,
  onOpen,
  horizontal = false,
}: {
  target: AlphaTargetData;
  isSelected: boolean;
  onOpen: (mint: string) => void;
  horizontal?: boolean; // If true, apply fixed width for horizontal scrolling
}) {
  const isMobile = useIsMobile();
  const displayImage = normalizeCoinImageUri(target.imageUri, target.mint);
  const displayName = target.name || `Token ${target.mint.slice(0, 4)}`;
  const displaySymbol = target.coin || target.mint.slice(0, 4).toUpperCase();

  // Vary image size based on market cap for visual interest
  const marketCap = target.mcUsd || 0;
  const imageSize = marketCap > 500000 ? "size-36" : marketCap > 250000 ? "size-32" : marketCap > 100000 ? "size-28" : marketCap > 50000 ? "size-24" : marketCap > 25000 ? "size-20" : marketCap > 10000 ? "size-16" : "size-14";
  const mobileImageSize = isMobile ? "size-20" : imageSize;

  // Random visual effects for engagement
  const randomEffect = useMemo(() => {
    const effects = [
      "hover:scale-110 hover:rotate-3",
      "hover:scale-110 hover:-rotate-3",
      "hover:scale-110 hover:rotate-6",
      "hover:scale-110 hover:-rotate-6",
      "hover:scale-110",
      "hover:scale-110 hover:rotate-12",
    ];
    const hash = Array.from(target.mint).reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return effects[hash % effects.length];
  }, [target.mint]);

  const randomGlow = useMemo(() => {
    const glows = [
      "hover:shadow-[0_0_30px_rgba(16,185,129,0.6)]",
      "hover:shadow-[0_0_30px_rgba(168,85,247,0.6)]",
      "hover:shadow-[0_0_30px_rgba(245,158,11,0.6)]",
      "hover:shadow-[0_0_30px_rgba(59,130,246,0.6)]",
      "hover:shadow-[0_0_30px_rgba(236,72,153,0.6)]",
    ];
    const hash = Array.from(target.mint).reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return glows[hash % glows.length];
  }, [target.mint]);

  const randomPulse = useMemo(() => {
    const pulses = ["", "animate-pulse-slow", "animate-pulse"];
    const hash = Array.from(target.mint).reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return pulses[hash % pulses.length];
  }, [target.mint]);

  const imageAnimation = `transition-all duration-700 ease-out ${randomEffect} ${randomGlow} ${randomPulse}`;

  const detailsLeftOffset = {
    "size-36": "left-56",
    "size-32": "left-52",
    "size-28": "left-48",
    "size-24": "left-44",
    "size-20": "left-36",
    "size-16": "left-28",
    "size-14": "left-24",
  }[imageSize] || "left-44";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(target.mint)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen(target.mint);
      }}
      className={`group border-b border-[var(--color-border-subtle)] text-left transition-all relative cursor-pointer outline-none hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] hover:border-emerald-500/20 ${
        isSelected ? "bg-emerald-500/[0.04]" : "hover:bg-white/[0.02]"
      } ${horizontal ? "shrink-0 min-w-[280px] sm:min-w-[320px] rounded-2xl border-x border-t mx-2 h-[280px]" : "w-full h-[280px]"} ${
        isMobile && !horizontal ? "px-3 py-2" : !horizontal ? "px-4 py-3" : ""
      }`}
    >
      {isSelected && !horizontal && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 shadow-[2px_0_10px_rgba(16,185,129,0.3)] z-20" />
      )}
      {isSelected && horizontal && (
        <div className="absolute inset-0 rounded-2xl border-2 border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)] pointer-events-none z-20" />
      )}

      {/* Chart takes full space as background */}
      <div className="absolute inset-0 bg-black/60 overflow-hidden border border-white/5 z-0 rounded-2xl">
        <MiniCandleChart
          mint={target.mint}
          height={280}
          rightOffset={isMobile || horizontal ? 10 : 30}
          showLogo={false}
        />
      </div>

      {/* Top left: name, description, CA, stats */}
      <div className={`absolute ${isMobile || horizontal ? "left-4 top-4 right-12" : "left-4 top-4 max-w-[280px]"} z-10 flex flex-col gap-1`}>
        <div className="flex items-center gap-2 leading-none">
          {target.badge && (
            <span className="text-[12px]">{target.badge}</span>
          )}
          <span className={`font-black bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent animate-gradient-x ${isMobile || horizontal ? "text-[13px]" : "text-[16px]"}`}>
            {displaySymbol}
          </span>
          {target.at ? (
            <span className="text-[8px] font-mono text-emerald-400 bg-emerald-500/10 px-1 py-0.5 rounded shrink-0">
              {fmtTimeAgo(target.at)}
            </span>
          ) : null}
        </div>
        <span className={`font-bold bg-gradient-to-r from-white/80 to-white/50 bg-clip-text text-transparent truncate ${isMobile || horizontal ? "text-[10px]" : "text-[11px]"}`}>
          {displayName}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(target.mint);
          }}
          className={`font-mono text-[8.5px] text-white/30 hover:text-white/70 truncate text-left transition-colors cursor-pointer active:scale-95 flex items-center gap-1 w-full max-w-[120px]`}
          title="Click to copy CA"
        >
          {target.mint.slice(0, 6)}...{target.mint.slice(-6)}
          <Copy className="size-2 opacity-50 shrink-0" />
        </button>

        {target.desc && (
          <span className="text-[9px] text-white/60 line-clamp-2 mt-1 leading-snug">
            {target.desc}
          </span>
        )}

        <div className="flex flex-wrap items-center gap-1.5 mt-1 max-w-[280px]">
          {target.multiplier != null && (
            <span className="flex items-center gap-1 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[8.5px] font-mono font-bold text-amber-400 border border-amber-500/40">
              {target.multiplier.toFixed(1)}x Peak
            </span>
          )}
          {target.confidence != null && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[8.5px] font-mono font-bold text-emerald-400 border border-emerald-500/40">
              {target.confidence.toFixed(0)}% Conf
            </span>
          )}
          {target.whaleSol != null && target.whaleSol > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-cyan-500/20 px-1.5 py-0.5 text-[8.5px] font-mono font-bold text-cyan-400 border border-cyan-500/40">
              🐋 {target.whaleSol.toFixed(1)} SOL
            </span>
          )}
        </div>
      </div>

      {/* Bottom left: image */}
      <div
        className={`absolute ${isMobile || horizontal ? "left-4 bottom-4" : "left-4 bottom-4"} shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_0_40px_rgba(0,0,0,0.8),0_0_20px_rgba(16,185,129,0.3)] ${mobileImageSize} ${imageAnimation} z-10`}
      >
        {displayImage ? (
          <img
            src={displayImage}
            alt={displaySymbol}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[var(--color-fg-dim)]/20">
            <Zap className={isMobile || horizontal ? "size-4" : "size-6"} />
          </div>
        )}
      </div>

      {/* Bottom right: stats */}
      <div
        className={`absolute ${isMobile || horizontal ? "left-28 right-4 bottom-4" : `${detailsLeftOffset} right-4 bottom-4`} z-10 flex flex-col gap-1 items-end`}
      >
        {target.mcUsd != null && (
          <div className="flex items-center gap-1">
            <span className={`font-mono font-black text-emerald-400 ${isMobile || horizontal ? "text-[11px]" : "text-[12px]"}`}>
              ${Math.round(target.mcUsd).toLocaleString()}
            </span>
            <span className={`font-mono text-white/40 ${isMobile || horizontal ? "text-[9px]" : "text-[9px]"}`}>MCAP</span>
          </div>
        )}
        <div className="flex flex-col items-end gap-0.5 mt-1">
          {target.top1 != null && (
            <span className="font-mono text-[9px] text-white/50">
              Whale: <span className="text-amber-400 font-bold">{target.top1.toFixed(1)}%</span>
            </span>
          )}
          {target.creatorPct != null && (
            <span className="font-mono text-[9px] text-white/50">
              Dev: <span className="text-white/70">{target.creatorPct.toFixed(1)}%</span>
            </span>
          )}
          {target.baselineMcUsd != null && (
            <span className="font-mono text-[9px] text-white/50">
              Base: <span className="text-white/70">${Math.round(target.baselineMcUsd).toLocaleString()}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
